/**
 * Form validation utilities
 * A robust validation system for various form types in the application
 */

/**
 * Validates if a value is not empty
 * @param {string|array} value - The value to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const isNotEmpty = (value) => {
  if (value === null || value === undefined) return false;
  
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  
  if (typeof value === 'object') {
    return Object.keys(value).length > 0;
  }
  
  return true;
};

/**
 * Validates an email address
 * @param {string} email - The email to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidEmail = (email) => {
  if (!email) return false;
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

/**
 * Validates a phone number with (XXX) XXX-XXXX format
 * @param {string} phone - The phone number to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidPhone = (phone) => {
  if (!phone) return false;
  // Allow for (XXX) XXX-XXXX format
  const re = /^\(\d{3}\) \d{3}-\d{4}$/;
  return re.test(phone);
};

/**
 * Validates a zip code
 * @param {string} zipCode - The zip code to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidZipCode = (zipCode) => {
  if (!zipCode) return false;
  const re = /^\d{5}(-\d{4})?$/;
  return re.test(zipCode);
};

/**
 * Validates that a string is at least a minimum length
 * @param {string} value - The string to validate
 * @param {number} minLength - The minimum length required
 * @returns {boolean} - True if valid, false otherwise
 */
export const isMinLength = (value, minLength) => {
  if (!value || typeof value !== 'string') return false;
  return value.length >= minLength;
};

/**
 * Validates that a string does not exceed a maximum length
 * @param {string} value - The string to validate
 * @param {number} maxLength - The maximum length allowed
 * @returns {boolean} - True if valid, false otherwise
 */
export const isMaxLength = (value, maxLength) => {
  if (!value || typeof value !== 'string') return true; // Empty is valid for max length
  return value.length <= maxLength;
};

/**
 * Validates that a number is within a range
 * @param {number} value - The number to validate
 * @param {number} min - The minimum value
 * @param {number} max - The maximum value
 * @returns {boolean} - True if valid, false otherwise
 */
export const isInRange = (value, min, max) => {
  if (value === null || value === undefined) return false;
  const num = Number(value);
  if (isNaN(num)) return false;
  return num >= min && num <= max;
};

/**
 * Checks if a string is a valid date in the format YYYY-MM-DD
 * @param {string} dateString - The date string to validate
 * @returns {boolean} - True if the date is valid, false otherwise
 */
export const isValidDate = (dateString) => {
  if (!dateString) return false;
  
  // Check format
  const dateFormat = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateFormat.test(dateString)) return false;
  
  // Check if it's a valid date
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return false;
  
  // Extract parts to validate day/month
  const parts = dateString.split('-');
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);
  
  // Create a date with those parts and check if matches original
  const reconstructed = new Date(year, month - 1, day);
  return reconstructed.getFullYear() === year &&
         reconstructed.getMonth() === month - 1 &&
         reconstructed.getDate() === day;
};

/**
 * Validates that a date is in the future
 * @param {string} date - The date string to validate (YYYY-MM-DD)
 * @returns {boolean} - True if valid, false otherwise
 */
export const isFutureDate = (date) => {
  if (!isValidDate(date)) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(date);
  return d >= today;
};

/**
 * Validates that a date is in the past
 * @param {string} date - The date string to validate (YYYY-MM-DD)
 * @returns {boolean} - True if valid, false otherwise
 */
export const isPastDate = (date) => {
  if (!isValidDate(date)) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(date);
  return d < today;
};

/**
 * Validates that a URL is valid
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidUrl = (url) => {
  if (!url) return false;
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Creates a validation object for a form field
 * @param {any} value - The value to validate
 * @param {string} errorMessage - The error message to display
 * @param {function} validationFn - The validation function to use
 * @returns {object} - An object with error and errorMessage properties
 */
export const validateField = (value, errorMessage, validationFn) => {
  const isValid = validationFn(value);
  return {
    error: !isValid,
    errorMessage: isValid ? '' : errorMessage
  };
};

/**
 * Creates a combined validation function from multiple validation functions
 * @param {...function} fns - Validation functions to combine
 * @returns {function} - Combined validation function
 */
export const combineValidators = (...fns) => {
  return (value) => fns.every(fn => fn(value));
};

/**
 * Validation schema for common form fields
 * @type {Object}
 */
export const validationSchemas = {
  /**
   * Username validation schema
   */
  username: (value) => combineValidators(
    isNotEmpty,
    (val) => isMinLength(val, 3),
    (val) => isMaxLength(val, 20)
  )(value),
  
  /**
   * Password validation schema
   */
  password: (value) => {
    if (!isNotEmpty(value)) return false;
    const hasMinLength = isMinLength(value, 8);
    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasDigit = /\d/.test(value);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);
    return hasMinLength && hasUpperCase && hasLowerCase && hasDigit && hasSpecialChar;
  },
  
  /**
   * Email validation schema
   */
  email: (value) => combineValidators(isNotEmpty, isValidEmail)(value),
  
  /**
   * Phone validation schema
   */
  phone: (value) => combineValidators(isNotEmpty, isValidPhone)(value),
  
  /**
   * Zip code validation schema
   */
  zipCode: (value) => combineValidators(isNotEmpty, isValidZipCode)(value)
};

/**
 * Form validation for QuoteForm
 * @param {object} formData - The quote form data
 * @returns {object} - An object with errors for each field
 */
export const validateQuoteForm = (formData) => {
  const errors = {
    name: validateField(formData.name, 'First Name is required', isNotEmpty),
    lastName: validateField(formData.lastName, 'Last Name is required', isNotEmpty),
    email: validateField(formData.email, 'Email is required', validationSchemas.email),
    phone: validateField(formData.phone, 'Phone is required', validationSchemas.phone),
    insured: validateField(formData.insured, 'Please select if you are insured', isNotEmpty)
  };

  // Only validate property address if required for this insurance type
  if (['Home Insurance', 'Flood Insurance', 'Auto Insurance'].includes(formData.productType)) {
    errors.propertyAddress = validateField(
      formData.propertyAddress, 
      'Property Address is required', 
      isNotEmpty
    );
  }

  // Calculate if the form has any errors
  const hasErrors = Object.values(errors).some(field => field.error);

  return {
    errors,
    hasErrors
  };
};

/**
 * Form validation for LoginForm
 * @param {object} formData - The login form data
 * @returns {object} - An object with errors for each field
 */
export const validateLoginForm = (formData) => {
  const errors = {
    email: validateField(formData.email, 'Email is required', validationSchemas.email),
    password: validateField(formData.password, 'Password is required', isNotEmpty)
  };

  const hasErrors = Object.values(errors).some(field => field.error);

  return {
    errors,
    hasErrors
  };
};

/**
 * Form validation for RegisterForm
 * @param {object} formData - The registration form data
 * @returns {object} - An object with errors for each field
 */
export const validateRegisterForm = (formData) => {
  const errors = {
    name: validateField(formData.name, 'First Name is required', isNotEmpty),
    lastName: validateField(formData.lastName, 'Last Name is required', isNotEmpty),
    email: validateField(formData.email, 'Email is required', validationSchemas.email),
    password: validateField(
      formData.password, 
      'Password must be at least 8 characters and include uppercase, lowercase, number, and special character', 
      validationSchemas.password
    ),
    passwordConfirmation: validateField(
      formData.passwordConfirmation, 
      'Passwords do not match', 
      (val) => val === formData.password
    )
  };

  const hasErrors = Object.values(errors).some(field => field.error);

  return {
    errors,
    hasErrors
  };
};

/**
 * Generic form validation based on a validation schema
 * @param {object} formData - The form data to validate
 * @param {object} schema - Validation schema mapping fields to validators and error messages
 * @returns {object} - An object with errors for each field
 */
export const validateForm = (formData, schema) => {
  const errors = {};
  
  Object.entries(schema).forEach(([field, config]) => {
    const { validator, errorMessage } = config;
    errors[field] = validateField(formData[field], errorMessage, validator);
  });
  
  const hasErrors = Object.values(errors).some(field => field.error);
  
  return {
    errors,
    hasErrors
  };
}; 